import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Plus } from 'lucide-react';
import { useBrandProductTags, BrandProductTag } from '@/hooks/useBrandProductTags';
import { useBrandTerminology } from '@/hooks/useBrandTerminology';

interface ProductTagSelectorProps {
  brandId: string;
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
  className?: string;
}

export function ProductTagSelector({ 
  brandId, 
  selectedTagIds, 
  onTagsChange,
  className = "" 
}: ProductTagSelectorProps) {
  const [newTagName, setNewTagName] = useState('');
  const { getTerminology } = useBrandTerminology();
  const { tags, createTag, isCreating } = useBrandProductTags(brandId);

  const selectedTags = tags.filter(tag => selectedTagIds.includes(tag.id));
  const availableTags = tags.filter(tag => !selectedTagIds.includes(tag.id));

  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onTagsChange(selectedTagIds.filter(id => id !== tagId));
    } else {
      onTagsChange([...selectedTagIds, tagId]);
    }
  };

  const removeTag = (tagId: string) => {
    onTagsChange(selectedTagIds.filter(id => id !== tagId));
  };

  const handleCreateTag = () => {
    if (!newTagName.trim()) return;
    
    // Generate a random color for the new tag
    const colors = [
      '#3b82f6', '#ef4444', '#10b981', '#f59e0b', 
      '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
    ];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    
    createTag({
      brand_id: brandId,
      name: newTagName.trim(),
      color: randomColor
    });
    
    setNewTagName('');
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <Label>{getTerminology('tags', { plural: true })}</Label>
        
        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <div className="mt-2 mb-3">
            <div className="flex flex-wrap gap-2">
              {selectedTags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="default"
                  style={{ backgroundColor: tag.color, color: 'white' }}
                  className="flex items-center gap-1"
                >
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => removeTag(tag.id)}
                    className="ml-1 hover:bg-white/20 rounded p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Available Tags */}
        {availableTags.length > 0 && (
          <div className="mb-3">
            <p className="text-sm text-muted-foreground mb-2">Available {getTerminology('tags', { plural: true })}:</p>
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="outline"
                  style={{ color: tag.color, borderColor: tag.color }}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => toggleTag(tag.id)}
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Add New Tag */}
        <div className="flex gap-2">
          <Input
            placeholder={`Add new ${getTerminology('tags', { plural: false })}...`}
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateTag();
              }
            }}
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCreateTag}
            disabled={!newTagName.trim() || isCreating}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}